import { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";

import aboutHero from "../assets/villa1.jpeg";

import { useLocation } from "react-router-dom";

import { db } from "../firebase";
import {
    collection,
    addDoc,
    onSnapshot,
    Timestamp,
} from "firebase/firestore";

function BookNow() {

    const location = useLocation();

    useEffect(() => {
        AOS.init({ duration: 900, once: true });
    }, []);

    useEffect(() => {
        if (location.state) {
            setFormData((prev) => ({
                ...prev,
                country: location.state.selectedCountry || "",
                arrival: location.state.checkInDate
                    ? new Date(location.state.checkInDate)
                    : null,
                departure: location.state.checkOutDate
                    ? new Date(location.state.checkOutDate)
                    : null,
            }));
        }
    }, [location.state]);

    // ---------------- STATES ----------------
    const [bookedRanges, setBookedRanges] = useState([]);

    const [formData, setFormData] = useState({
        roomType: "Family villa",
        name: "",
        email: "",
        phone: "",
        country: "",
        arrival: null,
        departure: null,
        adults: "",
        children: "",
    });

    // ---------------- FETCH BOOKINGS ----------------
    useEffect(() => {
        const unsub = onSnapshot(collection(db, "bookings"), (snapshot) => {
            const ranges = snapshot.docs.map((doc) => {
                const data = doc.data();
                const start =
                    data.arrival instanceof Timestamp
                        ? data.arrival.toDate()
                        : new Date(data.arrival);
                const end =
                    data.departure instanceof Timestamp
                        ? data.departure.toDate()
                        : new Date(data.departure);
                return { start, end };
            });
            setBookedRanges(ranges);
        });

        return () => unsub();
    }, []);

    // ---------------- GET ALL BOOKED DAYS ----------------
    const getBookedDates = () => {
        let dates = [];
        bookedRanges.forEach(({ start, end }) => {
            let current = new Date(start);
            while (current <= end) {
                dates.push(new Date(current));
                current.setDate(current.getDate() + 1);
            }
        });
        return dates;
    };

    // ---------------- HANDLE INPUT ----------------
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // ---------------- SAVE BOOKING ----------------
    const handleSaveBooking = async () => {
        if (
            !formData.name ||
            !formData.email ||
            !formData.phone ||
            !formData.country ||
            !formData.arrival ||
            !formData.departure ||
            !formData.adults
        ) {
            alert("Please fill all required fields");
            return false;
        }

        if (formData.arrival >= formData.departure) {
            alert("Departure date must be after arrival date");
            return false;
        }

        // Phone validation (Sri Lanka format: 07XXXXXXXX)
        const phonePattern = /^07\d{8}$/;
        if (!phonePattern.test(formData.phone)) {
            alert("Please enter a valid Sri Lankan phone number (e.g., 07XXXXXXXX)");
            return false;
        }

        try {
            await addDoc(collection(db, "bookings"), {
                roomType: formData.roomType,
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                country: formData.country,
                arrival: Timestamp.fromDate(formData.arrival),
                departure: Timestamp.fromDate(formData.departure),
                adults: formData.adults,
                children: formData.children || 0,
                createdAt: Timestamp.now(),
                status: "pending",
            });

            alert("Booking saved successfully!");
            return true;
        } catch (error) {
            console.error(error);
            alert("Booking failed");
            return false;
        }
    };

    // ---------------- WHATSAPP ----------------
    const handleWhatsApp = () => {
        const message = `
            📌 *New Booking Request*
            🏨 Room Type: ${formData.roomType}
            👤 Name: ${formData.name}
            📧 Email: ${formData.email}
            📞 Phone: ${formData.phone}
            🌍 Country: ${formData.country}
            📅 Arrival: ${formData.arrival.toDateString()}
            📅 Departure: ${formData.departure.toDateString()}
            🧑 Adults: ${formData.adults}
            👶 Children: ${formData.children || 0}
        `;

        const whatsappNumber = "94764806045";
        window.open(
            `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`,
            "_blank"
        );
    };

    // ---------------- SUBMIT ----------------
    const handleSubmit = async (e) => {
        e.preventDefault();
        const saved = await handleSaveBooking();
        if (saved) {
            handleWhatsApp();
            setFormData({
                roomType: "Family villa",
                name: "",
                email: "",
                phone: "",
                country: "",
                arrival: null,
                departure: null,
                adults: "",
                children: "",
            });
        }
    };

    // ---------------- JSX ----------------
    return (
        <div className="w-full overflow-hidden">
            {/* HERO */}
            <section className="relative h-[40vh] w-full">
                <img
                    src={aboutHero}
                    alt="Book Now"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60" />
                <div className="relative z-10 flex h-full items-center justify-center">
                    <h1 className="text-4xl md:text-5xl border border-[#c97b3a] bg-black/40 px-10 py-6 font-bold text-white">
                        Book Now
                    </h1>
                </div>
            </section>

            {/* FORM + CALENDAR */}
            <section className="bg-white py-20 px-4">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">

                    {/* FORM */}
                    <form
                        onSubmit={handleSubmit}
                        className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 text-lg"
                    >
                        <select
                            name="roomType"
                            value={formData.roomType}
                            onChange={handleChange}
                            className="border rounded-lg px-4 py-3"
                        >
                            <option>Family Villa (Up to 4 Guests)</option>
                            <option>Single Room Villa (For Couples)</option>
                        </select>

                        <input name="name" value={formData.name} onChange={handleChange} placeholder="Name" required className="border rounded-lg px-4 py-3" />
                        <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email" required className="border rounded-lg px-4 py-3" />
                        <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Whatsapp Number" required className="border rounded-lg px-4 py-3" />
                        <input name="country" value={formData.country} onChange={handleChange} placeholder="Country" required className="border rounded-lg px-4 py-3" />

                        <DatePicker
                            selected={formData.arrival}
                            onChange={(date) => setFormData({ ...formData, arrival: date })}
                            minDate={new Date()}

                            placeholderText="Arrival Date"
                            className="border rounded-lg px-4 py-3 w-full"
                        />

                        <DatePicker
                            selected={formData.departure}
                            onChange={(date) => setFormData({ ...formData, departure: date })}
                            minDate={formData.arrival || new Date()}

                            placeholderText="Departure Date"
                            className="border rounded-lg px-4 py-3 w-full"
                        />

                        <input type="number" name="adults" value={formData.adults} onChange={handleChange} placeholder="Adults" required className="border rounded-lg px-4 py-3" />
                        <input type="number" name="children" value={formData.children} onChange={handleChange} placeholder="Children" className="border rounded-lg px-4 py-3" />

                        <div className="md:col-span-2 text-center mt-6">
                            <button className="bg-[#c97b3a] text-white px-16 py-4 text-xl rounded-lg">
                                Book Now
                            </button>
                        </div>
                    </form>

                    {/* INLINE CALENDAR */}
                    <div className="border rounded-lg p-6 bg-gray-50 max-w-lg mx-auto">
                        <h3 className="text-xl font-semibold mb-3 text-[#c97b3a] text-center">
                            Availability Calendar
                        </h3>

                        <DatePicker
                            inline
                            minDate={new Date()}
                            highlightDates={[
                                { "booked-date": getBookedDates() }
                            ]}
                        />
                    </div>
                </div>
            </section>

            {/* ================= WHATSAPP FLOAT BUTTON ================= */}
            <a
                href="https://wa.me/94764806045?text=I%20saw%20your%20website.%20Can%20you%20give%20me%20information%20regarding%20your%20villa."
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-5 right-5 z-50"
            >
                <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/WhatsApp.svg/512px-WhatsApp.svg.png"
                    alt="WhatsApp"
                    className="h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg transition hover:scale-110"
                />
            </a>

        </div>
    );
}

export default BookNow;
